import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EventCard from "@/components/feature/EventCard";
import { categories, siteInfo } from "@/mocks/site";
import { events } from "@/mocks/events";

const SORT_OPTIONS = [
  { value: "latest", label: "最新发布" },
  { value: "heat", label: "热度最高" },
  { value: "readtime", label: "篇幅最长" },
];

function heatValue(heat: string): number {
  return parseFloat(heat.replace(/[^\d.]/g, "")) || 0;
}

export default function EventList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const category = searchParams.get("category") ?? "all";
  const sort = searchParams.get("sort") ?? "latest";
  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== "all") {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setSearchParams(next, { replace: true });
  };

  const normalizedQuery = query.trim().toLowerCase();
  const filtered = events
    .filter((event) => (category === "all" ? true : event.categorySlug === category))
    .filter((event) => {
      if (!normalizedQuery) {
        return true;
      }
      const haystack = [
        event.title,
        event.summary,
        event.categoryName,
        ...event.tags,
        ...event.figures.map((figure) => figure.name),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(normalizedQuery);
    })
    .sort((a, b) => {
      if (sort === "heat") {
        return heatValue(b.heat) - heatValue(a.heat);
      }
      if (sort === "readtime") {
        return b.readTime - a.readTime;
      }
      return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
    });

  const activeCategoryName =
    category === "all" ? "全部事件" : categories.find((item) => item.slug === category)?.name ?? "全部事件";

  useDocumentMeta({
    title: `${activeCategoryName}复盘合集｜网红事件完整经过与深度解析 - 吃瓜复盘局`,
    description:
      "吃瓜复盘局事件库收录带货翻车、情感纠纷、人设争议、版权抄袭、直播风波等方向的网红事件完整经过，支持按分类与关键词筛选，快速找到你想看懂的前因后果。",
    keywords: "网红事件库,网红事件完整经过,带货翻车复盘,情感纠纷复盘,人设争议解析",
    canonical: `${siteInfo.domain}/events`,
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "网红事件库",
        url: `${siteInfo.domain}/events`,
        description: "按分类与热度整理网红事件完整经过与深度解析合集。",
        isPartOf: { "@type": "WebSite", url: `${siteInfo.domain}/` },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${siteInfo.domain}/` },
          { "@type": "ListItem", position: 2, name: "事件库", item: `${siteInfo.domain}/events` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "网红事件复盘列表",
        itemListElement: filtered.map((event, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: event.title,
          url: `${siteInfo.domain}/events/${event.slug}`,
        })),
      },
    ],
  });

  return (
    <div className="bg-background-50">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-8 md:py-12">
          <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "事件库" }]} />
          <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            网红事件库
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-600 md:text-base">
            按分类与热度收录每一个事件的完整经过。点开任意一篇，都能看到时间线、关键人物、争议焦点与深度解析。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-8 md:py-12">
        <div className="flex flex-col gap-5">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => updateParam("category", "all")}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                category === "all"
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              全部
            </button>
            {categories.map((item) => (
              <button
                key={item.slug}
                type="button"
                onClick={() => updateParam("category", item.slug)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  category === item.slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {item.name}
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <form
              onSubmit={(event) => {
                event.preventDefault();
                updateParam("q", keyword.trim());
              }}
              className="flex w-full items-center gap-2 sm:max-w-md"
            >
              <div className="relative flex-1">
                <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                <input
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索标题、人物或标签"
                  className="h-11 w-full rounded-md border border-background-300 bg-background-50 pl-10 pr-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <button
                type="submit"
                className="h-11 whitespace-nowrap rounded-md bg-secondary-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600"
              >
                搜索
              </button>
            </form>

            <div className="flex items-center gap-3">
              <span className="whitespace-nowrap text-sm text-foreground-500">
                共 {filtered.length} 篇
              </span>
              <label className="sr-only" htmlFor="sort-select">
                排序方式
              </label>
              <select
                id="sort-select"
                value={sort}
                onChange={(event) => updateParam("sort", event.target.value)}
                className="h-11 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {filtered.length > 0 ? (
          <div className="mt-9 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((event) => (
              <EventCard key={event.slug} event={event} />
            ))}
          </div>
        ) : (
          <div className="mt-12 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">
              没有找到匹配的事件
            </h2>
            <p className="mt-2 max-w-md text-sm text-foreground-600">
              试试更换关键词，或切换到其他分类。也可以浏览全部事件，看看最近有哪些值得关注的复盘。
            </p>
            <button
              type="button"
              onClick={() => setSearchParams(new URLSearchParams(), { replace: true })}
              className="mt-6 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              查看全部事件
            </button>
          </div>
        )}
      </div>
    </div>
  );
}